import { useState, type FormEvent } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatKarachiDateTime } from '@/lib/dates'
import { formatPKR, pkrToPaisa } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { buildExpense } from './expensePayload'
import { saveExpense } from './saveExpense'

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'
const labelCls = 'mb-1 block text-xs font-medium text-slate-400'

/** General expenses ledger (feeds net profit in Reports). */
export function ExpensesPage() {
  const ws = getActiveWorkspaceId()
  const expenses = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).expenses.where('workspace_id').equals(ws).toArray() : []), [ws])
  const categories = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).categories.where('workspace_id').equals(ws).toArray() : []), [ws])

  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const list = [...(expenses ?? [])].filter((e) => !e.deleted_at).sort((a, b) => b.spent_at.localeCompare(a.spent_at))
  const total = list.reduce((s, e) => s + e.amount_paisa, 0)

  async function onAdd(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!ws) return
    let paisa: number
    try {
      paisa = pkrToPaisa(amount)
    } catch {
      return setError('Enter a valid amount.')
    }
    if (paisa <= 0) return setError('Amount must be positive.')
    setBusy(true)
    try {
      const summary = await saveExpense(ws, buildExpense({ workspace_id: ws, amount_paisa: paisa, category_id: categoryId || null, note: note || null }))
      if (summary.rejected.length > 0) {
        setError(`Not saved: ${summary.rejected[0].result.status}`)
        return
      }
      setAmount('')
      setNote('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page title="Expenses" note={`Total: ${formatPKR(total)} (deducted once in net profit)`}>
      <form onSubmit={onAdd} className="mb-4 space-y-2 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls} htmlFor="x-amount">Amount (PKR) *</label>
            <input id="x-amount" className={inputCls} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="3000" />
          </div>
          <div>
            <label className={labelCls} htmlFor="x-cat">Category</label>
            <select id="x-cat" className={inputCls} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">General</option>
              {(categories ?? []).filter((c) => !c.archived_at).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls} htmlFor="x-note">Note</label>
          <input id="x-note" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="shop rent, bills…" />
        </div>
        {error ? <p role="alert" className="text-xs text-rose-300">{error}</p> : null}
        <button type="submit" disabled={busy} className="w-full rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
          {busy ? '…' : 'Add expense'}
        </button>
      </form>

      {list.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-700 px-4 py-8 text-center text-sm text-slate-500">No expenses yet.</p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/40">
          {list.map((e) => (
            <li key={e.id} className="flex items-center justify-between px-3 py-2.5 text-sm">
              <div>
                <p className="text-slate-100">{e.note ?? 'Expense'}</p>
                <p className="text-[11px] text-slate-500">{formatKarachiDateTime(e.spent_at)}</p>
              </div>
              <span className="tabular-nums text-rose-400">−{formatPKR(e.amount_paisa, { symbol: false })}</span>
            </li>
          ))}
        </ul>
      )}
    </Page>
  )
}
